import { JSX, useContext, useEffect, useState } from "react";
import { cloneDeep } from "lodash";
import { ClientContext } from "@app/admin/contexts/ClientContext";
import { BasicState } from "@app/geography";
import GeographyService from "@app/geography/geographyService";
import LoadingSpinner from "@app/shared/LoadingSpinner";
import { CheckboxStateEnum, QESCheckbox } from "@app/shared/qes-standards/forms/Checkbox";
import { MultiselectItem, MultiselectOnChangeResponse, QESMultiSelect } from "@app/shared/qes-standards/forms/Multiselect";

interface ClientBenchmarkingProps {
  disabled?: boolean;
}

const ClientAffiliationAuthorizedStates = (props: ClientBenchmarkingProps): JSX.Element => {
  const [availableStates, setAvailableStates] = useState<BasicState[]>([]);
  const [selectAllChecked, setSelectAllChecked] = useState<boolean>(false);
  const [stateOptions, setStateOptions] = useState<{ [key: string]: MultiselectItem }>(null);
  const [statesLoading, setStatesLoading] = useState<boolean>(true);

  const clientContext = useContext(ClientContext);

  const geographyService = new GeographyService();

  useEffect(() => {
    geographyService.getStatesForDroplist().then((data) => {
      if (data?.length) {
        const states: { [key: string]: MultiselectItem } = {};
        const unwantedStates = ["GU", "VI"];
        data = data.filter(x => !unwantedStates.includes(x.abbreviation) || !x.abbreviation);

        data.forEach((r) => Object.assign(states,
          {
            [r.abbreviation]: {
              currentState: clientContext.providerAffiliationAuthorizedStates?.includes(r.abbreviation) ? 
              CheckboxStateEnum.Checked : 
              CheckboxStateEnum.Unchecked,
              displayValue: r.name,
            }
          }
        ));

        setAvailableStates(data);
        setStateOptions(states);
        setStatesLoading(false);
      }
    });
  }, []);

  useEffect(() => {
    const newStateOptions = cloneDeep(stateOptions);
    if (!!stateOptions) {
       Object.keys(newStateOptions)
        .forEach(x => newStateOptions[x].currentState = clientContext.providerAffiliationAuthorizedStates?.includes(x) ? 
          CheckboxStateEnum.Checked : 
          CheckboxStateEnum.Unchecked);

      setStateOptions(newStateOptions);
    }
  }, [clientContext.providerAffiliationAuthorizedStates]);

  const onSelectAllToggle = () => {
    const newSelectAllValue = !selectAllChecked;
    const newStateOptions = cloneDeep(stateOptions);

    Object.keys(newStateOptions)
      .forEach(x => newStateOptions[x].currentState = newSelectAllValue ? 
        CheckboxStateEnum.Checked : 
        CheckboxStateEnum.Unchecked);

    setStateOptions(newStateOptions);
    setSelectAllChecked(newSelectAllValue);
    clientContext.setProviderAffiliationAuthorizedStates(newSelectAllValue ? availableStates.map(x => x.abbreviation) : null);
  }

  const updateStates = (response: MultiselectOnChangeResponse): void => {
    setSelectAllChecked(response.checkedKeys?.length === availableStates.length);
    clientContext.setProviderAffiliationAuthorizedStates(response.checkedKeys)
  };

  return (
    <div>
      <div className="row">
        <div className="col-md-8">
          <div className="row">
            <div className="col-12">
              <QESCheckbox
                checked={selectAllChecked}
                containerClassName="mb-0 mt-3"
                disabled={props.disabled}
                label="Select All States"
                testId="client-admin__affiliation--select-all"
                onChange={() => onSelectAllToggle()}
              />
            </div>
          </div>
          <div className="row">
            <div className="col-12">
              {!statesLoading && !!stateOptions ? (
                <QESMultiSelect
                  containerClassName="mb-3 mt-3"
                  disabled={props.disabled}
                  label="Authorized States"
                  onChange={updateStates}
                  options={stateOptions}
                  required={true}
                  testId="client-admin__affiliation--states"
                />
              ) : (
                <LoadingSpinner />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ClientAffiliationAuthorizedStates;
